/**
 * URL Manager - Simple round-robin failover for server URLs
 *
 * Features:
 * - Cycles through multiple server URLs when connections fail
 * - All servers treated equally (no "primary" preference)
 * - Stays on current server until a problem occurs
 * - No automatic recovery attempts (just cycle on failure)
 *
 * Usage:
 *   const url = urlManager.getCurrentUrl('TZ', 'marketdata', urls);
 *   // ... try to connect ...
 *   // On success:
 *   urlManager.markSuccess('TZ', 'marketdata');
 *   // On failure:
 *   const nextUrl = urlManager.markFailureAndGetNext('TZ', 'marketdata', urls);
 */
class UrlManager {
  constructor() {
    // Track current active server index for each broker+type
    // Map key format: "TZ-marketdata" → index (0, 1, 2, etc.)
    this.activeIndexes = new Map();

    // Track server health statistics (for debugging/monitoring)
    // Map key format: "TZ-marketdata-0" → { failures: 2, lastFailure: timestamp, lastError: "..." }
    this.serverHealth = new Map();
  }

  /**
   * Get the composite key for broker+type
   * @private
   */
  _getKey(broker, type) {
    return `${broker}-${type}`;
  }

  /**
   * Get the health key for a specific server
   * @private
   */
  _getHealthKey(broker, type, index) {
    return `${broker}-${type}-${index}`;
  }

  /**
   * Get current active URL for a broker/service combination
   *
   * @param {string} broker - Broker name (TZ, XPI, CLEAR, RICO)
   * @param {string} type - Service type ('marketdata' or 'broker')
   * @param {string[]} urls - Array of server URLs from config
   * @returns {string} - Currently active URL
   * @throws {Error} - If no URLs are configured
   */
  getCurrentUrl(broker, type, urls) {
    if (!urls || urls.length === 0) {
      throw new Error(`[URLManager] No URLs configured for ${broker} ${type}`);
    }

    const key = this._getKey(broker, type);
    const activeIndex = this.activeIndexes.get(key) || 0;

    // Ensure index is within bounds (safety check)
    const safeIndex = Math.min(activeIndex, urls.length - 1);

    return urls[safeIndex];
  }

  /**
   * Get current server index (0-based)
   *
   * @param {string} broker - Broker name
   * @param {string} type - Service type ('marketdata' or 'broker')
   * @returns {number} - Current server index
   */
  getCurrentIndex(broker, type) {
    const key = this._getKey(broker, type);
    return this.activeIndexes.get(key) || 0;
  }

  /**
   * Mark current connection as successful
   * Resets failure counter for this server
   *
   * @param {string} broker - Broker name
   * @param {string} type - Service type ('marketdata' or 'broker')
   */
  markSuccess(broker, type) {
    const key = this._getKey(broker, type);
    const currentIndex = this.activeIndexes.get(key) || 0;
    const healthKey = this._getHealthKey(broker, type, currentIndex);

    // Reset failure count and record success
    this.serverHealth.set(healthKey, {
      failures: 0,
      lastSuccess: Date.now(),
      lastFailure: null,
      lastError: null,
    });

    console.log(
      `[URLManager] ✅ ${broker} ${type} server ${currentIndex} connected successfully`,
    );
  }

  /**
   * Mark current connection as failed and cycle to next server
   *
   * @param {string} broker - Broker name
   * @param {string} type - Service type ('marketdata' or 'broker')
   * @param {string[]} urls - Array of server URLs
   * @param {Error} error - Connection error (optional)
   * @returns {string|null} - Next URL to try, or null if no alternates
   */
  markFailureAndGetNext(broker, type, urls, error = null) {
    if (!urls || urls.length === 0) {
      console.error(`[URLManager] No URLs configured for ${broker} ${type}`);
      return null;
    }

    const key = this._getKey(broker, type);
    const currentIndex = this.activeIndexes.get(key) || 0;
    const healthKey = this._getHealthKey(broker, type, currentIndex);

    // Record failure
    const health = this.serverHealth.get(healthKey) || {
      failures: 0,
      lastFailure: null,
    };
    health.failures++;
    health.lastFailure = Date.now();
    health.lastError = error?.message || "Unknown error";
    this.serverHealth.set(healthKey, health);

    console.warn(
      `[URLManager] ❌ ${broker} ${type} server ${currentIndex} failed:`,
      urls[currentIndex],
      `(${health.failures} total failures)`,
      error?.message || "",
    );

    // If only one URL, can't cycle - return the same URL
    if (urls.length === 1) {
      console.warn(
        `[URLManager] ⚠️  Only one ${broker} ${type} server configured - cannot failover`,
      );
      return urls[0];
    }

    // Cycle to next server (round-robin)
    const nextIndex = (currentIndex + 1) % urls.length;
    this.activeIndexes.set(key, nextIndex);

    console.log(
      `[URLManager] 🔄 Cycling ${broker} ${type} to server ${nextIndex}:`,
      urls[nextIndex],
    );

    return urls[nextIndex];
  }

  /**
   * Reset all state for a specific broker+type
   * Useful for testing or manual override
   *
   * @param {string} broker - Broker name
   * @param {string} type - Service type ('marketdata' or 'broker')
   */
  reset(broker, type) {
    const key = this._getKey(broker, type);
    this.activeIndexes.delete(key);

    // Clear all health entries for this broker+type
    const prefix = `${key}-`;
    for (const healthKey of this.serverHealth.keys()) {
      if (healthKey.startsWith(prefix)) {
        this.serverHealth.delete(healthKey);
      }
    }

    console.log(`[URLManager] 🔄 Reset state for ${broker} ${type}`);
  }

  /**
   * Get health statistics for debugging/monitoring
   *
   * @returns {Object} - Health statistics with active indexes and server health
   */
  getHealthStats() {
    return {
      activeIndexes: Object.fromEntries(this.activeIndexes),
      serverHealth: Object.fromEntries(this.serverHealth),
    };
  }

  /**
   * Print health report to console (for debugging)
   */
  printHealthReport() {
    const stats = this.getHealthStats();

    console.group("[URLManager] Health Report");

    console.log("Active Servers:");
    for (const [key, index] of Object.entries(stats.activeIndexes)) {
      console.log(`  ${key}: Server ${index}`);
    }

    console.log("\nServer Health:");
    for (const [key, health] of Object.entries(stats.serverHealth)) {
      const status = health.failures === 0 ? "✅" : "❌";
      console.log(
        `  ${status} ${key}: ${health.failures} failures`,
        health.lastFailure
          ? `(last: ${new Date(health.lastFailure).toISOString()})`
          : "",
      );
    }

    console.groupEnd();
  }
}

// Export singleton instance
export const urlManager = new UrlManager();

// Make available for debugging in browser console
if (typeof window !== "undefined") {
  window.urlManager = urlManager;
}
